import { parse } from 'qs';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import Lazy from 'yup/lib/Lazy';
import Reference from 'yup/lib/Reference';
import type { CastOptions } from 'yup/lib/schema';

export type RouteLocationState = {
  keepHeaderState?: boolean;
  preventScrollRestoration?: boolean;
};

export type ParamsConverter<T = unknown> = (params: {
  [P in keyof T]: string;
}) => T;

export type useSearchParamsOptions<T> = {
  converter?: ParamsConverter<T>;
};
export const useSearchParams = <T>({ converter }: useSearchParamsOptions<T> = {}): T => {
  const location = useLocation();
  const params = useMemo(() => {
    const parsed = parse(location.search, { ignoreQueryPrefix: true }) as {
      [P in keyof T]: string;
    };
    const converted = converter ? converter(parsed) : parsed;
    return converted as T;
  }, [converter, location.search]);

  return params;
};

yup.BaseSchema.prototype['castOrDefault'] = function (value: any, options?: CastOptions) {
  try {
    return this.isValidSync(value) ? this.cast(value, options) : this.getDefault();
  } catch (error) {
    return this.getDefault();
  }
};

yup.BaseSchema.prototype['json'] = function (json: any) {
  return this.transform(function (value) {
    if (JSON.stringify(json) === JSON.stringify(value)) {
      return value;
    } else {
      throw new yup.ValidationError('JSON Validation Error');
    }
  });
};

declare module 'yup' {
  interface BaseSchema {
    castOrDefault<T>(value: any, options?: CastOptions): T;
    json(json: any): BaseSchema;
  }

  type DomainShape<T = unknown> = {
    [K in keyof T]?: yup.AnySchema | Reference | Lazy<any, any>;
  };
}

export const useScrollRestoration = () => {
  const history = useHistory<{ scroll: string }>();
  useEffect(() => {
    history.listen((location, action) => {
      const { state } = location;
      const { scroll } = state || {};
      if (action === 'PUSH') {
        window.scrollTo(0, 0);
      } else if (scroll === 'restore') {
        window.requestAnimationFrame(() => window.scrollTo(0, 0));
      }
    });
  }, [history]);
};
